/**
 * Hand-drawn shape components using Rough.js
 * Feature: Hand-drawn line style for critique photo markup
 *
 * React wrapper components that use Rough.js to render sketchy, hand-drawn shapes
 */

import { useEffect, useRef } from 'react';
import rough from 'roughjs';
import type { Options } from 'roughjs/bin/core';

/**
 * Common props for all hand-drawn shapes
 */
interface BaseHandDrawnProps {
  strokeColor: string;
  strokeWidth: number;
  isSelected: boolean;
  onMouseDown?: (e: React.MouseEvent<SVGElement, MouseEvent>) => void;
  className?: string;
}

/**
 * Calculate stroke width in viewBox units based on SVG pixel dimensions
 *
 * The viewBox uses percentage coordinates (0-100 width), but strokeWidth is in pixels.
 * We need to scale strokeWidth to viewBox units to match the non-scaling-stroke behavior.
 */
function calculateViewBoxStrokeWidth(
  svgElement: SVGSVGElement,
  strokeWidthPx: number
): number {
  const bbox = svgElement.getBoundingClientRect();
  const viewBoxWidth = 100; // Our viewBox uses 0-100 for width

  // Calculate how many pixels = 1 viewBox unit
  const pixelsPerViewBoxUnit = bbox.width / viewBoxWidth;

  // Convert pixel stroke width to viewBox units
  const strokeWidthViewBox = strokeWidthPx / pixelsPerViewBoxUnit;

  return strokeWidthViewBox;
}

/**
 * Get Rough.js options from shape style properties
 */
function getRoughOptions(
  svgElement: SVGSVGElement,
  strokeColor: string,
  strokeWidth: number,
  isSelected: boolean
): Options {
  // Scale stroke width from pixels to viewBox units
  const viewBoxStrokeWidth = calculateViewBoxStrokeWidth(svgElement, strokeWidth);

  return {
    stroke: strokeColor,
    strokeWidth: isSelected ? viewBoxStrokeWidth * 1.5 : viewBoxStrokeWidth,
    roughness: 1.0, // Natural hand-drawn feel (0.5-2.5 range)
    bowing: 1.0, // Natural curve intensity
    fill: 'none',
    fillStyle: 'solid',
  };
}

/**
 * Hand-drawn rectangle component
 */
interface HandDrawnRectangleProps extends BaseHandDrawnProps {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function HandDrawnRectangle({
  x,
  y,
  width,
  height,
  strokeColor,
  strokeWidth,
  isSelected,
  onMouseDown,
  className,
}: HandDrawnRectangleProps) {
  const gRef = useRef<SVGGElement>(null);

  useEffect(() => {
    if (!gRef.current) return;

    // Clear previous content
    gRef.current.replaceChildren();

    // Get the parent SVG element
    const svgElement = gRef.current.closest('svg');
    if (!svgElement) return;

    // Create Rough.js renderer
    const rc = rough.svg(svgElement);
    const options = getRoughOptions(svgElement, strokeColor, strokeWidth, isSelected);

    // Generate hand-drawn rectangle
    const drawable = rc.rectangle(x, y, width, height, options);

    // Append to group
    gRef.current.appendChild(drawable);
  }, [x, y, width, height, strokeColor, strokeWidth, isSelected]);

  return (
    <g
      ref={gRef}
      className={className}
      onMouseDown={onMouseDown}
      style={{ cursor: isSelected ? 'grab' : 'pointer', pointerEvents: 'auto' }}
    />
  );
}

/**
 * Hand-drawn circle component
 */
interface HandDrawnCircleProps extends BaseHandDrawnProps {
  cx: number;
  cy: number;
  r: number;
}

export function HandDrawnCircle({
  cx,
  cy,
  r,
  strokeColor,
  strokeWidth,
  isSelected,
  onMouseDown,
  className,
}: HandDrawnCircleProps) {
  const gRef = useRef<SVGGElement>(null);

  useEffect(() => {
    if (!gRef.current) return;

    // Clear previous content
    gRef.current.replaceChildren();

    // Get the parent SVG element
    const svgElement = gRef.current.closest('svg');
    if (!svgElement) return;

    // Create Rough.js renderer
    const rc = rough.svg(svgElement);
    const options = getRoughOptions(svgElement, strokeColor, strokeWidth, isSelected);

    // Generate hand-drawn circle (Rough.js uses diameter, not radius)
    const drawable = rc.circle(cx, cy, r * 2, options);

    // Append to group
    gRef.current.appendChild(drawable);
  }, [cx, cy, r, strokeColor, strokeWidth, isSelected]);

  return (
    <g
      ref={gRef}
      className={className}
      onMouseDown={onMouseDown}
      style={{ cursor: isSelected ? 'grab' : 'pointer', pointerEvents: 'auto' }}
    />
  );
}

/**
 * Hand-drawn line component (for arrow shaft)
 */
interface HandDrawnLineProps extends BaseHandDrawnProps {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export function HandDrawnLine({
  x1,
  y1,
  x2,
  y2,
  strokeColor,
  strokeWidth,
  isSelected,
  onMouseDown,
  className,
}: HandDrawnLineProps) {
  const gRef = useRef<SVGGElement>(null);

  useEffect(() => {
    if (!gRef.current) return;

    // Clear previous content
    gRef.current.replaceChildren();

    // Get the parent SVG element
    const svgElement = gRef.current.closest('svg');
    if (!svgElement) return;

    // Create Rough.js renderer
    const rc = rough.svg(svgElement);
    const options = getRoughOptions(svgElement, strokeColor, strokeWidth, isSelected);

    // Generate hand-drawn line
    const drawable = rc.line(x1, y1, x2, y2, options);

    // Append to group
    gRef.current.appendChild(drawable);
  }, [x1, y1, x2, y2, strokeColor, strokeWidth, isSelected]);

  return (
    <g
      ref={gRef}
      className={className}
      onMouseDown={onMouseDown}
      style={{ cursor: isSelected ? 'grab' : 'pointer', pointerEvents: 'auto' }}
    />
  );
}

/**
 * Hand-drawn polygon component (for arrow head)
 */
interface HandDrawnPolygonProps extends BaseHandDrawnProps {
  points: Array<[number, number]>;
}

export function HandDrawnPolygon({
  points,
  strokeColor,
  strokeWidth,
  isSelected,
  onMouseDown,
  className,
}: HandDrawnPolygonProps) {
  const gRef = useRef<SVGGElement>(null);

  useEffect(() => {
    if (!gRef.current) return;

    // Clear previous content
    gRef.current.replaceChildren();

    // Get the parent SVG element
    const svgElement = gRef.current.closest('svg');
    if (!svgElement) return;

    // Create Rough.js renderer
    const rc = rough.svg(svgElement);
    const options = {
      ...getRoughOptions(svgElement, strokeColor, strokeWidth, isSelected),
      fill: strokeColor, // Arrow heads should be filled
      fillStyle: 'solid' as const,
    };

    // Generate hand-drawn polygon
    const drawable = rc.polygon(points, options);

    // Append to group
    gRef.current.appendChild(drawable);
  }, [points, strokeColor, strokeWidth, isSelected]);

  return (
    <g
      ref={gRef}
      className={className}
      onMouseDown={onMouseDown}
      style={{ cursor: isSelected ? 'grab' : 'pointer', pointerEvents: 'auto' }}
    />
  );
}
